
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const SocialMediaPage = () => {
  const [activeTab, setActiveTab] = useState('content-creation');

  const contentTypes = [
    {
      type: 'Property Listings',
      description: 'Eye-catching posts for new listings with compelling descriptions',
      example: 'Just listed! Stunning 4BR home with panoramic city views. Modern kitchen, spacious living areas, and a private garden oasis. Perfect for growing families! 🏡✨ #JustListed #DreamHome #RealEstate',
      platforms: ['Facebook', 'Instagram', 'LinkedIn', 'Twitter']
    },
    {
      type: 'Market Updates',
      description: 'Informative posts about local market trends and insights',
      example: 'Market Update: Downtown condos are seeing a 15% increase in value this quarter! Great time for sellers to capitalize on the strong demand. Contact me for a free market analysis! 📈 #MarketUpdate #RealEstateNews',
      platforms: ['LinkedIn', 'Facebook', 'Twitter']
    },
    {
      type: 'Client Testimonials',
      description: 'Social proof content featuring happy client experiences',
      example: '"Working with [Agent Name] was incredible! They found us our dream home in just 2 weeks. Professional, knowledgeable, and truly cared about our needs." - The Johnson Family ⭐⭐⭐⭐⭐ #HappyClients #Testimonial',
      platforms: ['Facebook', 'Instagram', 'Google My Business']
    },
    {
      type: 'Educational Content',
      description: 'Helpful tips and advice for buyers and sellers',
      example: '🏠 First-time buyer tip: Get pre-approved for your mortgage before house hunting! It shows sellers you\'re serious and helps you move quickly in competitive markets. Need recommendations for trusted lenders? DM me! #FirstTimeBuyer #RealEstateTips',
      platforms: ['Instagram', 'Facebook', 'TikTok', 'LinkedIn']
    }
  ];

  const features = [
    {
      icon: 'ri-edit-line',
      title: 'AI Content Generation',
      description: 'Create engaging posts, captions, and descriptions tailored to each platform and audience.'
    },
    {
      icon: 'ri-image-line',
      title: 'Visual Optimization',
      description: 'Automatically resize and optimize images for different social media platforms.'
    },
    {
      icon: 'ri-calendar-schedule-line',
      title: 'Smart Scheduling',
      description: 'Post at optimal times for maximum engagement based on your audience analytics.'
    },
    {
      icon: 'ri-hashtag',
      title: 'Hashtag Research',
      description: 'Discover trending and relevant hashtags to increase your content reach.'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Performance Analytics',
      description: 'Track engagement, reach, and conversion metrics across all platforms.'
    },
    {
      icon: 'ri-team-line',
      title: 'Multi-Platform Management',
      description: 'Manage all your social media accounts from one centralized dashboard.'
    }
  ];

  const platforms = [
    { name: 'Facebook', icon: 'ri-facebook-fill', color: 'text-blue-600' },
    { name: 'Instagram', icon: 'ri-instagram-line', color: 'text-pink-600' },
    { name: 'LinkedIn', icon: 'ri-linkedin-fill', color: 'text-blue-700' },
    { name: 'Twitter', icon: 'ri-twitter-fill', color: 'text-blue-400' },
    { name: 'TikTok', icon: 'ri-tiktok-line', color: 'text-black' },
    { name: 'YouTube', icon: 'ri-youtube-fill', color: 'text-red-600' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">
                Social Media Content Creator
              </h1>
              <p className="text-xl mb-8">
                Generate engaging social media content for your real estate business with AI-powered copywriting, 
                visual optimization, and automated scheduling across all major platforms.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/demo" 
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Try Content Generator
                </Link>
                <Link 
                  to="/contact" 
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Start Free Trial
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/cac9d7d991da33affd3ae663e164cfad.jpg"
                alt="Social Media Content Dashboard"
                className="w-full rounded-lg shadow-2xl object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'content-creation', label: 'Content Creation' },
              { id: 'features', label: 'Features' },
              { id: 'platforms', label: 'Platforms' },
              { id: 'analytics', label: 'Analytics' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Creation Tab */}
      {activeTab === 'content-creation' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">AI-Generated Content Examples</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See how our AI creates engaging, platform-specific content for your real estate business.
              </p>
            </div>
            
            <div className="space-y-8">
              {contentTypes.map((content, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4">{content.type}</h3>
                      <p className="text-gray-600 mb-6">{content.description}</p>
                      
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Recommended Platforms:</h4>
                        <div className="flex flex-wrap gap-2">
                          {content.platforms.map((platform, platformIndex) => (
                            <span key={platformIndex} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                              {platform}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Generated Content Example:</h4>
                      <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
                        <p className="text-gray-700 italic">{content.example}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Tab */}
      {activeTab === 'features' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Powerful Social Media Features</h2>
              <p className="text-xl text-gray-600">
                Everything you need to create, schedule, and optimize your social media presence.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Platforms Tab */}
      {activeTab === 'platforms' && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Supported Platforms</h2>
              <p className="text-xl text-gray-600">
                Create and publish content across all major social media platforms from one dashboard.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {platforms.map((platform, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow">
                  <i className={`${platform.icon} text-5xl ${platform.color} mb-4`}></i>
                  <h3 className="text-xl font-semibold text-gray-900">{platform.name}</h3>
                </div>
              ))}
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-semibold text-center mb-8">Platform-Specific Optimization</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Automatic Formatting</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      Character limits for each platform
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      Optimal image dimensions
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      Platform-specific hashtags
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      Engagement-optimized timing
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Content Adaptation</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      Tone adjustment per platform
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      Audience-specific messaging
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      Call-to-action optimization
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      Visual style matching
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Performance Analytics</h2>
              <p className="text-xl text-gray-600">
                Track your social media performance and optimize your content strategy with detailed insights.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Key Metrics Dashboard</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                    <span className="font-medium text-gray-900">Total Reach</span>
                    <span className="text-2xl font-bold text-blue-600">125,430</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                    <span className="font-medium text-gray-900">Engagement Rate</span>
                    <span className="text-2xl font-bold text-green-600">8.7%</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                    <span className="font-medium text-gray-900">Lead Generation</span>
                    <span className="text-2xl font-bold text-purple-600">47</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg">
                    <span className="font-medium text-gray-900">Conversion Rate</span>
                    <span className="text-2xl font-bold text-orange-600">3.2%</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Content Performance</h3>
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Top Performing Post</h4>
                    <p className="text-gray-600 text-sm">Luxury downtown condo listing</p>
                    <p className="text-blue-600 font-medium">2,340 engagements</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Best Engagement Time</h4>
                    <p className="text-gray-600 text-sm">Tuesday 7:00 PM</p>
                    <p className="text-green-600 font-medium">12.4% avg engagement</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Top Hashtag</h4>
                    <p className="text-gray-600 text-sm">#DreamHome</p>
                    <p className="text-purple-600 font-medium">890 impressions</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Boost Your Social Media Presence?</h3>
              <p className="text-xl mb-6">
                Start creating engaging content that converts followers into clients.
              </p>
              <Link 
                to="/contact" 
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Start Your Free Trial
              </Link>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default SocialMediaPage;
